/**
 * The level format: one plain-data map per module (arenaLevel.ts, registered
 * in levels.ts), JSON-shaped so
 * tools outside the game (the art factory's plate and tile bakers) can read
 * and write it. `validateLevel` lists what is wrong with one, `compileLevel`
 * turns a valid one into the run-time Arena the simulation, prediction and
 * renderer read (arena.ts).
 *
 * Units as everywhere: 1 u = UNIT_PX px, origin top-left, y down. Outside
 * the `w` x `h` bounds is wall. Format 1 has solid circles and boxes,
 * standing and deep water, a 1 u terrain grid, rooms, doors, spawns, the
 * floor plate's art name, objects the rules react to (braziers, rune
 * stones) and, for story levels, the story: what each room says, asks and
 * sends at you, and where the act ends.
 */

import type { Arena, DoorArea, Pillar, RoomArea, WaterZone } from './arena.js';
import type { Rect, Vec2 } from './math.js';
import type { EnemyKind } from './enemies.js';
import { ENEMY_KINDS } from './enemies.js';
import { LEVEL_MAX_SIZE } from './constants.js';

export const LEVEL_FORMAT = 1;

export type LevelCircle = { k: 'circle'; x: number; y: number; r: number };
export type LevelRect = { k: 'rect'; x: number; y: number; w: number; h: number };
export type LevelEllipse = { k: 'ellipse'; x: number; y: number; rx: number; ry: number };

/** A solid piece: blocks bodies, spells and sight. `art` names the prop set drawn on a circle. */
export interface LevelSolid { shape: LevelCircle | LevelRect; art?: string }
/** Standing water: wets anyone whose centre is inside. */
export interface LevelWater { shape: LevelEllipse | LevelRect }
export interface LevelRoom { id: string; rect: Rect }
/** A wall box that can open; solid while closed. `art` names the prop set drawn while it is shut. */
export interface LevelDoor { id: string; rect: Rect; open?: boolean; art?: string }
/**
 * Things the rules react to. A brazier is lit by fire and put out by water,
 * cold or steam; a rune stone is a checkpoint, claimed by walking up to it;
 * a boiler is filled with water and then heated (a story room's `boiler`
 * rule says what its pressure opens).
 */
export type LevelObject =
  | { k: 'brazier'; id: string; x: number; y: number }
  | { k: 'rune-stone'; id: string; x: number; y: number }
  | { k: 'boiler'; id: string; x: number; y: number };

/**
 * Terrain on a 1 u grid: `rows[y][x]`, exactly `h` rows of `w` characters.
 * '.' floor, '#' wall, '~' shallow water (wets), '=' deep water (too deep
 * to walk; spells cross it; cold freezes it into floor for a while), '%'
 * brambles (a wall that burns away), '_' chasm (no floor: feet can't cross
 * it, a body knocked in falls; lava fills it and cold sets the fill to
 * stone). Walls and water are merged into as few boxes as possible when the
 * level compiles.
 */
export interface LevelTiles { rows: string[] }

export const TILE = { floor: '.', wall: '#', water: '~', deep: '=', bramble: '%', chasm: '_' } as const;
const TILE_CHARS = new Set<string>(Object.values(TILE));

/** Enemies a story room sends: `count` of `kind` (scaled by party size), cycling the level gates listed. */
export interface StoryGroup { kind: EnemyKind; count: number; gates: number[] }

/** One room of a story level: what it says, asks and sends at you. */
export interface StoryRoom {
  /** A LevelRoom id. */
  room: string;
  /** Shown at the top while the party is in it. */
  title: string;
  /** The Book's lines the first time a player walks in. */
  say: string[];
  /** The objective line. */
  objective: string;
  /** Enemies that come when the room is first entered, or when its first brazier catches. */
  spawn?: { on: 'enter' | 'lit'; groups: StoryGroup[]; say?: string[] };
  /**
   * Light all of these braziers at once to open door `opens`. It stays shut
   * while the room still has enemies (a fighting room locks).
   */
  light?: { braziers: string[]; opens: string; say?: string[]; after: string };
  /**
   * Fill boiler `id` and keep it hot: its pressure builds for `seconds` of
   * heat, then door `opens` lifts. Push `i` of `pushes` comes when the
   * pressure reaches i / pushes.length (the first on entering).
   */
  boiler?: { id: string; opens: string; seconds: number; pushes: StoryGroup[][]; say?: string[]; after: string };
  /**
   * Doors that slam shut once the whole living party is in the room, and open
   * again when it is done (its enemies dead, its door open).
   */
  lock?: string[];
  /** A chasm room: the first row of chasm set to stone all the way across makes the Book speak and moves the objective on. */
  bridge?: { say?: string[]; after: string };
  /**
   * A boss room: `kind` comes out of gate `gate` when the room is first
   * entered (one, its HP scaled by party size) and calls `calls` while it
   * fights; `everflame` is the brazier that wakes in its last phase. Its
   * death ends the act: the brazier relights and the Book says `win`.
   * `phases[i]` is what the Book says as phase i + 2 begins, and
   * `objectives[i]` the objective line in phase i + 1.
   */
  boss?: {
    kind: EnemyKind;
    gate: number;
    calls: StoryGroup;
    everflame: string;
    phases: string[][];
    objectives: string[];
    win: string[];
  };
}

export interface LevelStory {
  /** "Act 1: The Drowned Cloister". */
  act: string;
  rooms: StoryRoom[];
  /** Walking into `rect` ends the act (a story without one ends when its boss dies). */
  exit?: { rect: Rect; say: string[] };
}

export interface LevelDef {
  format: typeof LEVEL_FORMAT;
  /** Lowercase letters, digits and dashes; the `room` frame carries it. */
  id: string;
  name: string;
  w: number;
  h: number;
  tiles?: LevelTiles;
  solids: LevelSolid[];
  water: LevelWater[];
  /** At least one; the arena has one room covering everything. */
  rooms: LevelRoom[];
  doors: LevelDoor[];
  /** Players spawn on a ring of radius `ring` around (x, y). */
  spawn: { x: number; y: number; ring: number };
  /** Enemy spawn points, cycled in order (DESIGN 4.4). */
  gates: Vec2[];
  /** Lobby training dummies (DESIGN 4.2). */
  dummies: Vec2[];
  /** PvP maps: the two teams' bases, each a spawn ring and the flag's plinth. */
  teams?: { name: string; spawn: { x: number; y: number; ring: number }; flag: Vec2 }[];
  /**
   * The floor: one baked plate (`plate`), or several side by side (`plates`,
   * for a map too big for one image); each is an image in the art manifest
   * whose origin places it on the map.
   */
  art: { plate?: string; plates?: string[] };
  objects?: LevelObject[];
  /** Story levels only. */
  story?: LevelStory;
}

const ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;
/** A spawn or dummy must keep this much floor around it. */
const STAND_CLEARANCE = 0.45;

function finite(...xs: number[]): boolean {
  return xs.every((x) => typeof x === 'number' && Number.isFinite(x));
}

function rectInside(b: Rect, w: number, h: number): boolean {
  return finite(b.x, b.y, b.w, b.h) && b.w > 0 && b.h > 0 && b.x >= 0 && b.y >= 0 && b.x + b.w <= w && b.y + b.h <= h;
}

function pointInside(p: Vec2, w: number, h: number): boolean {
  return finite(p.x, p.y) && p.x >= 0 && p.y >= 0 && p.x <= w && p.y <= h;
}

/** Does a disc at p overlap one of the level's solids (circles, boxes, closed doors, wall tiles)? */
function standsInSolid(def: LevelDef, walls: readonly Rect[], p: Vec2, r: number): boolean {
  for (const s of def.solids) {
    const sh = s.shape;
    if (sh.k === 'circle') {
      const dx = p.x - sh.x, dy = p.y - sh.y, rr = sh.r + r;
      if (dx * dx + dy * dy < rr * rr) return true;
    } else if (discInRect(p, r, sh)) return true;
  }
  for (const d of def.doors) if (!d.open && discInRect(p, r, d.rect)) return true;
  for (const b of walls) if (discInRect(p, r, b)) return true;
  return false;
}

function discInRect(p: Vec2, r: number, b: Rect): boolean {
  const qx = Math.min(Math.max(p.x, b.x), b.x + b.w);
  const qy = Math.min(Math.max(p.y, b.y), b.y + b.h);
  const dx = p.x - qx, dy = p.y - qy;
  return dx * dx + dy * dy < r * r;
}

/**
 * Merge every `ch` cell of the grid into boxes, greedily: from the first
 * free cell in reading order, run east as far as the cells go, then grow
 * south while the whole run continues. Deterministic, no overlaps, and the
 * boxes cover exactly the `ch` cells.
 */
export function mergeTiles(rows: readonly string[], ch: string): Rect[] {
  const h = rows.length;
  const w = rows.reduce((m, r) => Math.max(m, r.length), 0);
  return mergeCells(w, h, (x, y) => rows[y]?.[x] === ch);
}

/** Every cell index (y * w + x) of the grid holding tile `ch`, in reading order. */
export function tileCells(rows: readonly string[], ch: string): number[] {
  const w = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const out: number[] = [];
  rows.forEach((row, y) => { for (let x = 0; x < row.length; x++) if (row[x] === ch) out.push(y * w + x); });
  return out;
}

/** mergeTiles over any test of a w x h grid of 1 u cells. */
export function mergeCells(w: number, h: number, test: (x: number, y: number) => boolean): Rect[] {
  const used = new Uint8Array(w * h);
  const is = (x: number, y: number): boolean => x < w && y < h && used[y * w + x] === 0 && test(x, y);
  const out: Rect[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!is(x, y)) continue;
      let x1 = x + 1;
      while (is(x1, y)) x1++;
      let y1 = y + 1;
      for (;;) {
        let full = y1 < h;
        for (let cx = x; full && cx < x1; cx++) if (!is(cx, y1)) full = false;
        if (!full) break;
        y1++;
      }
      for (let cy = y; cy < y1; cy++) for (let cx = x; cx < x1; cx++) used[cy * w + cx] = 1;
      out.push({ x, y, w: x1 - x, h: y1 - y });
    }
  }
  return out;
}

/** Everything wrong with a level, as readable lines; empty when it is valid. */
export function validateLevel(def: LevelDef): string[] {
  const errs: string[] = [];
  if (def.format !== LEVEL_FORMAT) errs.push(`format must be ${LEVEL_FORMAT}`);
  if (typeof def.id !== 'string' || !ID_RE.test(def.id)) errs.push(`id '${String(def.id)}' must be lowercase letters, digits and dashes`);
  if (typeof def.name !== 'string' || def.name.trim() === '') errs.push('name is empty');
  const { w, h } = def;
  if (!finite(w, h) || w <= 0 || h <= 0 || w > LEVEL_MAX_SIZE || h > LEVEL_MAX_SIZE) {
    errs.push(`size ${w} x ${h} must be within 0..${LEVEL_MAX_SIZE}`);
    return errs; // everything below is checked against the bounds
  }

  let walls: Rect[] = [];
  if (def.tiles) {
    const rows = def.tiles.rows;
    if (!Number.isInteger(w) || !Number.isInteger(h)) errs.push('a level with tiles needs whole-unit w and h');
    if (rows.length !== h) errs.push(`tiles has ${rows.length} rows, expected ${h}`);
    rows.forEach((row, y) => {
      if (row.length !== w) errs.push(`tiles row ${y} is ${row.length} wide, expected ${w}`);
      for (const c of row) if (!TILE_CHARS.has(c)) { errs.push(`tiles row ${y} has unknown tile '${c}'`); break; }
    });
    // deep water, brambles and the chasm are no place to stand either
    walls = [...mergeTiles(rows, TILE.wall), ...mergeTiles(rows, TILE.deep), ...mergeTiles(rows, TILE.bramble), ...mergeTiles(rows, TILE.chasm)];
  }

  def.solids.forEach((s, i) => {
    const sh = s.shape;
    if (sh.k === 'circle') {
      if (!finite(sh.r) || sh.r <= 0 || !pointInside(sh, w, h)) errs.push(`solid ${i}: circle must have r > 0 and sit inside the level`);
    } else if (sh.k === 'rect') {
      if (!rectInside(sh, w, h)) errs.push(`solid ${i}: box must have a size and sit inside the level`);
    } else {
      errs.push(`solid ${i}: unknown shape`);
    }
    if (s.art !== undefined && (typeof s.art !== 'string' || s.art === '')) errs.push(`solid ${i}: art must be a set name`);
  });
  def.water.forEach((z, i) => {
    const sh = z.shape;
    if (sh.k === 'ellipse') {
      if (!finite(sh.rx, sh.ry) || sh.rx <= 0 || sh.ry <= 0 || !pointInside(sh, w, h)) errs.push(`water ${i}: ellipse must have radii > 0 and sit inside the level`);
    } else if (sh.k === 'rect') {
      if (!rectInside(sh, w, h)) errs.push(`water ${i}: box must have a size and sit inside the level`);
    } else {
      errs.push(`water ${i}: unknown shape`);
    }
  });

  if (def.rooms.length === 0) errs.push('a level needs at least one room');
  const roomIds = new Set<string>();
  for (const r of def.rooms) {
    if (!ID_RE.test(r.id)) errs.push(`room id '${r.id}' must be lowercase letters, digits and dashes`);
    if (roomIds.has(r.id)) errs.push(`room id '${r.id}' is used twice`);
    roomIds.add(r.id);
    if (!rectInside(r.rect, w, h)) errs.push(`room '${r.id}' must sit inside the level`);
  }
  const doorIds = new Set<string>();
  for (const d of def.doors) {
    if (!ID_RE.test(d.id)) errs.push(`door id '${d.id}' must be lowercase letters, digits and dashes`);
    if (doorIds.has(d.id)) errs.push(`door id '${d.id}' is used twice`);
    doorIds.add(d.id);
    if (!rectInside(d.rect, w, h)) errs.push(`door '${d.id}' must sit inside the level`);
    if (d.art !== undefined && (typeof d.art !== 'string' || d.art === '')) errs.push(`door '${d.id}': art must be a set name`);
  }

  const objectIds = new Map<string, LevelObject['k']>();
  for (const o of def.objects ?? []) {
    if (o.k !== 'brazier' && o.k !== 'rune-stone' && o.k !== 'boiler') { errs.push(`object '${String((o as { id?: unknown }).id)}' has an unknown kind`); continue; }
    if (!ID_RE.test(o.id)) errs.push(`object id '${o.id}' must be lowercase letters, digits and dashes`);
    if (objectIds.has(o.id)) errs.push(`object id '${o.id}' is used twice`);
    objectIds.set(o.id, o.k);
    if (!pointInside(o, w, h)) errs.push(`object '${o.id}' must sit inside the level`);
    // a boiler stands on its own footing (a solid circle), so only the walls count for it
    else if (standsInSolid(o.k === 'boiler' ? { ...def, solids: [] } : def, walls, o, STAND_CLEARANCE)) errs.push(`object '${o.id}' stands in a solid`);
  }

  if (def.story) errs.push(...validateStory(def, def.story, roomIds, doorIds, objectIds));

  const sp = def.spawn;
  if (!pointInside(sp, w, h) || !finite(sp.ring) || sp.ring < 0) {
    errs.push('spawn must sit inside the level with a ring >= 0');
  } else {
    // the four ring slots players stand on (players.ts spawnPoint), plus the centre
    const slots: Vec2[] = [{ x: sp.x, y: sp.y }, { x: sp.x, y: sp.y - sp.ring }, { x: sp.x + sp.ring, y: sp.y }, { x: sp.x, y: sp.y + sp.ring }, { x: sp.x - sp.ring, y: sp.y }];
    if (slots.some((p) => standsInSolid(def, walls, p, STAND_CLEARANCE))) errs.push('spawn ring overlaps a solid');
  }
  if (def.gates.length === 0) errs.push('a level needs at least one enemy gate');
  def.gates.forEach((g, i) => { if (!pointInside(g, w, h)) errs.push(`gate ${i} must sit inside the level`); });
  def.dummies.forEach((d, i) => {
    if (!pointInside(d, w, h)) errs.push(`dummy ${i} must sit inside the level`);
    else if (standsInSolid(def, walls, d, STAND_CLEARANCE)) errs.push(`dummy ${i} stands in a solid`);
  });
  if (def.teams) {
    if (def.teams.length !== 2) errs.push('teams: a PvP map has exactly two');
    def.teams.forEach((t, i) => {
      if (typeof t.name !== 'string' || t.name.trim() === '') errs.push(`team ${i}: needs a name`);
      if (!pointInside(t.flag, w, h) || standsInSolid(def, walls, t.flag, STAND_CLEARANCE)) errs.push(`team ${i}: the flag must stand on open floor`);
      const s = t.spawn;
      if (!pointInside(s, w, h) || !finite(s.ring) || s.ring < 0) errs.push(`team ${i}: spawn must sit inside the level with a ring >= 0`);
      else {
        const slots: Vec2[] = [{ x: s.x, y: s.y }, { x: s.x, y: s.y - s.ring }, { x: s.x + s.ring, y: s.y }, { x: s.x, y: s.y + s.ring }, { x: s.x - s.ring, y: s.y }];
        if (slots.some((p) => standsInSolid(def, walls, p, STAND_CLEARANCE))) errs.push(`team ${i}: spawn ring overlaps a solid`);
      }
    });
  }
  if (def.art.plate !== undefined && (typeof def.art.plate !== 'string' || def.art.plate === '')) errs.push('art.plate must be an image name');
  if (def.art.plates !== undefined && (def.art.plates.length === 0 || def.art.plates.some((p) => typeof p !== 'string' || p === ''))) errs.push('art.plates must be image names');
  return errs;
}

function validateStory(
  def: LevelDef,
  story: LevelStory,
  roomIds: ReadonlySet<string>,
  doorIds: ReadonlySet<string>,
  objectIds: ReadonlyMap<string, LevelObject['k']>,
): string[] {
  const errs: string[] = [];
  const text = (s: unknown): boolean => typeof s === 'string' && s.trim() !== '';
  if (!text(story.act)) errs.push('story: act is empty');
  if (story.rooms.length === 0) errs.push('story: no rooms');
  const seen = new Set<string>();
  story.rooms.forEach((r, i) => {
    const at = `story room ${i} ('${r.room}')`;
    if (!roomIds.has(r.room)) errs.push(`${at}: no such room`);
    if (seen.has(r.room)) errs.push(`${at}: listed twice`);
    seen.add(r.room);
    if (!text(r.title) || !text(r.objective)) errs.push(`${at}: needs a title and an objective`);
    if (!r.say.every(text)) errs.push(`${at}: empty line`);
    for (const g of r.spawn?.groups ?? []) {
      if (!ENEMY_KINDS.includes(g.kind) || g.kind === 'dummy') errs.push(`${at}: unknown enemy '${String(g.kind)}'`);
      if (!Number.isInteger(g.count) || g.count < 1) errs.push(`${at}: a group needs a whole count >= 1`);
      if (g.gates.length === 0 || g.gates.some((n) => !Number.isInteger(n) || n < 0 || n >= def.gates.length)) errs.push(`${at}: group gates must index the level's gates`);
    }
    if (r.light) {
      if (r.light.braziers.length === 0 || r.light.braziers.some((b) => objectIds.get(b) !== 'brazier')) errs.push(`${at}: light needs braziers that exist`);
      if (!doorIds.has(r.light.opens)) errs.push(`${at}: light opens no such door '${r.light.opens}'`);
      if (!text(r.light.after)) errs.push(`${at}: light needs the objective that comes after`);
    }
    if (r.boiler) {
      const b = r.boiler;
      if (objectIds.get(b.id) !== 'boiler') errs.push(`${at}: boiler '${b.id}' is not a boiler`);
      if (!doorIds.has(b.opens)) errs.push(`${at}: boiler opens no such door '${b.opens}'`);
      if (!finite(b.seconds) || b.seconds <= 0) errs.push(`${at}: boiler needs seconds > 0`);
      if (!text(b.after)) errs.push(`${at}: boiler needs the objective that comes after`);
      for (const push of b.pushes) for (const g of push) {
        if (!ENEMY_KINDS.includes(g.kind) || g.kind === 'dummy') errs.push(`${at}: unknown enemy '${String(g.kind)}'`);
        if (!Number.isInteger(g.count) || g.count < 1) errs.push(`${at}: a group needs a whole count >= 1`);
        if (g.gates.length === 0 || g.gates.some((n) => !Number.isInteger(n) || n < 0 || n >= def.gates.length)) errs.push(`${at}: group gates must index the level's gates`);
      }
    }
    for (const d of r.lock ?? []) if (!doorIds.has(d)) errs.push(`${at}: locks no such door '${d}'`);
    if (r.bridge && !text(r.bridge.after)) errs.push(`${at}: bridge needs the objective that comes after`);
    if (r.boss) {
      const b = r.boss;
      if (!ENEMY_KINDS.includes(b.kind) || b.kind === 'dummy') errs.push(`${at}: unknown boss '${String(b.kind)}'`);
      const gateOk = (n: number): boolean => Number.isInteger(n) && n >= 0 && n < def.gates.length;
      if (!gateOk(b.gate)) errs.push(`${at}: the boss gate must index the level's gates`);
      if (!ENEMY_KINDS.includes(b.calls.kind) || b.calls.kind === 'dummy') errs.push(`${at}: unknown enemy '${String(b.calls.kind)}'`);
      if (!Number.isInteger(b.calls.count) || b.calls.count < 1) errs.push(`${at}: a group needs a whole count >= 1`);
      if (b.calls.gates.length === 0 || !b.calls.gates.every(gateOk)) errs.push(`${at}: group gates must index the level's gates`);
      if (objectIds.get(b.everflame) !== 'brazier') errs.push(`${at}: the boss's everflame must be a brazier`);
      if (!b.objectives.every(text) || b.objectives.length === 0) errs.push(`${at}: the boss needs objective lines`);
      if (!b.win.every(text) || b.win.length === 0 || !b.phases.every((p) => p.every(text))) errs.push(`${at}: empty line`);
    }
  });
  if (story.exit && !rectInside(story.exit.rect, def.w, def.h)) errs.push('story: the exit must sit inside the level');
  if (!story.exit && !story.rooms.some((r) => r.boss)) errs.push('story: needs an exit or a boss room to end the act');
  return errs;
}

/**
 * The run-time map of a valid level (throws, listing the problems, for an
 * invalid one). Circles become pillars; boxes, wall tiles and closed doors
 * become blocks; water shapes and water tiles become water zones. Order is
 * kept (solids, then tiles, then doors) so collision runs the same every time.
 */
export function compileLevel(def: LevelDef): Arena {
  const errs = validateLevel(def);
  if (errs.length > 0) throw new Error(`level '${String(def.id)}' is not valid:\n- ${errs.join('\n- ')}`);
  const pillars: Pillar[] = [];
  const walls: Rect[] = [];
  for (const s of def.solids) {
    const sh = s.shape;
    if (sh.k === 'circle') pillars.push(s.art !== undefined ? { x: sh.x, y: sh.y, r: sh.r, art: s.art } : { x: sh.x, y: sh.y, r: sh.r });
    else walls.push({ x: sh.x, y: sh.y, w: sh.w, h: sh.h });
  }
  const water: WaterZone[] = def.water.map(({ shape: sh }) =>
    sh.k === 'ellipse' ? { k: 'ellipse', x: sh.x, y: sh.y, rx: sh.rx, ry: sh.ry } : { k: 'rect', x: sh.x, y: sh.y, w: sh.w, h: sh.h });
  let deepCells: number[] = [];
  let brambleCells: number[] = [];
  let pitCells: number[] = [];
  if (def.tiles) {
    walls.push(...mergeTiles(def.tiles.rows, TILE.wall));
    for (const b of mergeTiles(def.tiles.rows, TILE.water)) water.push({ k: 'rect', ...b });
    deepCells = tileCells(def.tiles.rows, TILE.deep);
    brambleCells = tileCells(def.tiles.rows, TILE.bramble);
    pitCells = tileCells(def.tiles.rows, TILE.chasm);
  }
  const doors: DoorArea[] = def.doors.map((d) => {
    const door: DoorArea = { id: d.id, rect: { ...d.rect }, open: d.open === true };
    if (d.art !== undefined) door.art = d.art;
    return door;
  });
  const rooms: RoomArea[] = def.rooms.map((r) => ({ id: r.id, rect: { ...r.rect } }));
  const base: Arena = {
    id: def.id,
    w: def.w,
    h: def.h,
    pillars,
    walls,
    blocks: [],
    deepCells,
    brambleCells,
    pitCells,
    deep: [],
    water,
    rooms,
    doors,
    objects: (def.objects ?? []).map((o) => ({ ...o })),
    gates: def.gates.map((g) => ({ x: g.x, y: g.y })),
    spawn: { x: def.spawn.x, y: def.spawn.y },
    spawnRing: def.spawn.ring,
    dummies: def.dummies.map((d) => ({ x: d.x, y: d.y })),
    teams: (def.teams ?? []).map((t) => ({ name: t.name, spawn: { x: t.spawn.x, y: t.spawn.y }, spawnRing: t.spawn.ring, flag: { x: t.flag.x, y: t.flag.y } })),
    plates: def.art.plates ? [...def.art.plates] : def.art.plate !== undefined ? [def.art.plate] : [],
  };
  if (def.art.plate !== undefined) base.plate = def.art.plate;
  const open = new Set(doors.filter((d) => d.open).map((d) => d.id));
  return withTerrain(base, { open, frozen: new Set() });
}

/** These cells of a w-wide grid, merged into boxes. */
function mergeSet(w: number, h: number, cells: ReadonlySet<number>): Rect[] {
  if (cells.size === 0) return [];
  return mergeCells(w, h, (x, y) => cells.has(y * w + x));
}

/** What changes on a map while it is played (the server's terrain state, the snapshot's `tr`). */
export interface TerrainView {
  /** Ids of the doors standing open (every other door is shut). */
  open: ReadonlySet<string>;
  /** Deep-water cells iced over into floor. */
  frozen: ReadonlySet<number>;
  /** Bramble cells burnt away to ash. */
  ash?: ReadonlySet<number>;
  /** Chasm cells filled with lava and set to stone. */
  filled?: ReadonlySet<number>;
  /** Rooms whose floor is flooded (a boss's flood): standing water over every cell that isn't deep. */
  flooded?: ReadonlySet<string>;
}

/**
 * The map as it stands right now: the open doors open and every other one
 * shut, frozen water and stone-filled chasm walkable, burnt brambles gone.
 * Pure and deterministic, so the server and client prediction build the
 * same walls from the same snapshot. Keeps the level's id; a new object
 * each call. Blocks are the walls, then the shut doors, then the brambles;
 * `deep` is the open water and the open chasm (feet only); a flooded
 * room adds its floor to the standing water.
 */
export function withTerrain(base: Arena, t: TerrainView): Arena {
  const doors: DoorArea[] = base.doors.map((d) => ({ ...d, rect: { ...d.rect }, open: t.open.has(d.id) }));
  const blocks: Rect[] = base.walls.map((b) => ({ ...b }));
  for (const d of doors) if (!d.open) blocks.push({ ...d.rect });
  if (base.brambleCells.length > 0) {
    const standing = new Set<number>();
    for (const c of base.brambleCells) if (!t.ash?.has(c)) standing.add(c);
    blocks.push(...mergeSet(base.w, base.h, standing));
  }
  const feet = new Set<number>();
  for (const c of base.deepCells) if (!t.frozen.has(c)) feet.add(c);
  for (const c of base.pitCells) if (!t.filled?.has(c)) feet.add(c);
  let water = base.water;
  if (t.flooded && t.flooded.size > 0) {
    const deep = new Set(base.deepCells);
    const wet = new Set<number>();
    for (const r of base.rooms) {
      if (!t.flooded.has(r.id)) continue;
      for (let y = Math.max(0, r.rect.y); y < Math.min(base.h, r.rect.y + r.rect.h); y++) {
        for (let x = Math.max(0, r.rect.x); x < Math.min(base.w, r.rect.x + r.rect.w); x++) if (!deep.has(y * base.w + x)) wet.add(y * base.w + x);
      }
    }
    water = [...base.water, ...mergeSet(base.w, base.h, wet).map((b): WaterZone => ({ k: 'rect', ...b }))];
  }
  return { ...base, doors, blocks, deep: mergeSet(base.w, base.h, feet), water };
}
